/**
 * @file src/core/boot.js
 * bootLesson() — điểm vào duy nhất của mọi tiết. Nó lắp toàn bộ core + shell,
 * tạo ra `ctx` rồi gọi 5 hàm của tiết theo đúng vòng đời.
 *
 * Thứ tự KHÔNG được đổi:
 *   detect WebGL2 → Engine → Shell → i18n → assets → lesson.init() → ready
 *
 * Vì sao detect WebGL2 TRƯỚC khi tạo Engine: nếu tạo Engine rồi mới biết máy
 * không hỗ trợ, ta đã tốn một context và không kịp hiện poster fallback.
 */

import * as THREE from 'three';
import { Engine } from './Engine.js';
import { createTracker } from './track.js';
import { createEmitter } from './events.js';
import { createSettings } from './settings.js';
import { createI18n } from './i18n.js';
import { createTelemetry } from './telemetry.js';
import { createProgress } from './progress.js';
import { createBridge } from './bridge.js';
import { createPicker } from './picking.js';
import { createAssetLoader } from './loaders/AssetLoader.js';
import { createAudio } from './audio.js';
import { detectQuality } from './quality.js';
import { watchContextLoss } from './contextLoss.js';
import { mountShell } from '../shell/Shell.js';
import { showStaticFallback } from '../shell/ErrorScreen.js';
import { SHELL_VI } from '../shell/i18n/vi.js';
import { SHELL_EN } from '../shell/i18n/en.js';

/** Từ điển của SHELL theo ngôn ngữ. Thêm ngôn ngữ mới = thêm một dòng ở đây. */
const SHELL_DICTS = /** @type {Record<string, Record<string, string>>} */ ({
  vi: SHELL_VI,
  en: SHELL_EN,
});

/** Version API của LessonContext. Tiết khai engineRange khớp với số này. */
export const ENGINE_API = '0.1';
export const CORE_VERSION = '0.1.0';

/** Khoá asset quy ước cho ảnh nền màn hình tải. Tiết khai thì hiện, không khai thì bỏ qua. */
export const LOADING_BG_KEY = 'img.loadingBg';

/** Capabilities mà core hiện tại hỗ trợ. FE dùng mảng này để bật/tắt tính năng. */
export const CAPABILITIES = Object.freeze([
  'error.v1',
  'progress.v1',
  'i18n.setLanguage',
  'lifecycle.setActive',
  'deeplink.step',
]);

/**
 * @typedef {import('./track.js').Tracker} Tracker
 * @typedef {import('./quality.js').QualityTier} QualityTier
 */

/**
 * @typedef {Object} LessonMeta
 * @property {string} id
 * @property {string} subject
 * @property {number} grade
 * @property {string} engineRange
 * @property {number} stepCount
 * @property {string[]} assets
 * @property {{near?: number, far?: number, exposure?: number, presetLights?: boolean,
 *             shadows?: boolean, minPolarAngle?: number, maxPolarAngle?: number}} [engine]
 *   TUỲ CHỌN. Ghi đè thông số Engine cho riêng tiết này. Chỉ khai khi mặc định
 *   (near 0.6 · far 100 · exposure 1.0) không đủ — vd tiết dạng CẢNH có vòm trời bao
 *   quanh thì khoảng (near, far) phải chứa cả điểm nhìn rất gần lẫn vòm trời rất xa.
 *   `presetLights: false` = tắt bộ đèn chuẩn của nền tảng (AGENT.md mục 11b) và tiết TỰ
 *   chịu trách nhiệm toàn bộ ánh sáng. Chỉ dùng cho tiết có nhu cầu chiếu sáng đặc thù,
 *   và phải ghi rõ lý do ngay tại chỗ khai.
 *   `min/maxPolarAngle` (radian) = chặn góc xoay dọc. Model dạng CẢNH (mảng đất, sa bàn)
 *   phải đặt `maxPolarAngle ≈ π/2` — mặt dưới rỗng, lật xuống là học sinh thấy đáy.
 *   Đọc TRƯỚC khi tạo Engine nên phải nằm ở `meta`, không đặt trong init().
 */

/**
 * Một tiết học. Ngoài 5 hàm dưới đây, tiết được phép giữ state riêng trên `this`
 * (vd `this.model`, `this.labels`) — vì vậy typedef có index signature. Đánh đổi
 * có chủ ý: mất type-check cho state riêng, đổi lấy việc dev viết tiết tự nhiên
 * mà không phải khai trước từng field.
 * @typedef {Object} LessonModule
 * @property {LessonMeta} meta
 * @property {(ctx: LessonContext) => void | Promise<void>} init
 * @property {(dt: number, elapsed: number, ctx: LessonContext) => void} [update]
 * @property {(w: number, h: number, ctx: LessonContext) => void} [onResize]
 * @property {() => void} [dispose]
 */

/**
 * Kiểu dùng TRONG file lesson.js: LessonModule + state tự do trên `this`.
 * Tiết khai `/** @type {AnyLessonModule} *\/` thay vì LessonModule.
 * @typedef {LessonModule & Record<string, any>} AnyLessonModule
 */

/**
 * Mọi thứ shell cấp cho tiết. ĐÂY LÀ CONTRACT — thêm field thì được, đổi/xoá thì không.
 * @typedef {Object} LessonContext
 * @property {typeof THREE} THREE
 * @property {import('three').WebGLRenderer} renderer
 * @property {import('three').Scene} scene
 * @property {import('three').PerspectiveCamera} camera
 * @property {import('three/addons/controls/OrbitControls.js').OrbitControls} controls
 * @property {Engine} engine
 * @property {HTMLCanvasElement} canvas
 * @property {HTMLElement} domRoot
 * @property {import('./loaders/AssetLoader.js').AssetApi} assets
 * @property {import('./audio.js').AudioApi} audio  Phát âm thanh; tự theo tuỳ chọn âm thanh/âm lượng
 * @property {import('./i18n.js').TranslateFn} t
 * @property {string} lang
 * @property {import('../shell/Shell.js').UiApi} ui
 * @property {import('./progress.js').ProgressApi} progress
 * @property {import('./telemetry.js').Telemetry} telemetry
 * @property {import('./settings.js').Settings} settings
 * @property {import('./events.js').Emitter} events
 * @property {import('./picking.js').Picker} picker
 * @property {(code: string, o: {fatal?: boolean, retryable?: boolean, messageKey: string, detail?: string}) => void} reportError
 * @property {<T>(obj: T) => T} track
 * @property {(fn: () => void) => void} onDispose
 * @property {AbortSignal} signal
 * @property {URLSearchParams} params
 * @property {QualityTier} quality
 * @property {boolean} isActive
 * @property {boolean} reducedMotion
 * @property {{width: number, height: number, dpr: number}} viewport
 */

/**
 * @typedef {Object} BootOptions
 * @property {HTMLElement} root              Thẻ bọc (shell + canvas nằm trong)
 * @property {LessonModule} lesson
 * @property {import('./loaders/AssetLoader.js').AssetManifest} manifest
 * @property {Record<string, Record<string, string>>} dicts   { vi: {...}, en: {...} }
 * @property {Record<string, any>} info      Thông tin bài giảng từ content/index
 * @property {number} [buildVersion=0]
 * @property {string} [baseUrl='/']
 * @property {boolean} [debug=false]
 */

/**
 * @param {BootOptions} o
 * @returns {Promise<{ ctx: LessonContext, engine: Engine, dispose: () => void }>}
 */
export async function bootLesson(o) {
  const t0 = performance.now();
  const params = new URLSearchParams(location.search);
  const debug = o.debug ?? params.has('debug');
  const buildVersion = o.buildVersion ?? 0;

  const tracker = createTracker({ dev: debug });
  const emitter = createEmitter();
  const abort = new AbortController();

  const settings = createSettings({ lang: params.get('lang') ?? 'vi', emitter });
  const languages = Object.keys(o.dicts);
  const lang = languages.includes(settings.get('lang')) ? settings.get('lang') : 'vi';
  // Trộn chuỗi của SHELL với chuỗi của TIẾT. Tiết ghi đè được nếu thật sự cần.
  // Trước đây hai nhánh của ternary GIỐNG HỆT nhau nên mọi ngôn ngữ đều nhận
  // SHELL_VI — bật tiếng Anh thì nội dung tiết sang Anh nhưng taskbar và bảng cài
  // đặt vẫn nguyên tiếng Việt. Giờ tra theo bảng SHELL_DICTS; ngôn ngữ nào chưa có
  // từ điển shell thì rơi về tiếng Việt (chấp nhận được, và lộ ra ngay để đi bổ sung).
  /** @type {Record<string, Record<string, string>>} */
  const mergedDicts = {};
  for (const [l, d] of Object.entries(o.dicts)) {
    mergedDicts[l] = { ...(SHELL_DICTS[l] ?? SHELL_VI), ...d };
  }
  const i18n = createI18n({ lang, dicts: mergedDicts });
  const telemetry = createTelemetry({ lessonId: o.lesson.meta.id, debug });

  // ---------- Bridge phải tạo TRƯỚC mọi thứ có thể lỗi ----------
  /** @type {Engine | null} */
  let engine = null;
  const bridge = createBridge({
    lessonId: o.lesson.meta.id,
    buildVersion,
    coreVersion: CORE_VERSION,
    engineApi: ENGINE_API,
    contentVersion: String(o.info?.contentVersion ?? ''),
    capabilities: [...CAPABILITIES],
    languages,
    onSetLanguage: (l) => {
      if (!languages.includes(l)) return false;
      settings.set('lang', l);
      return true;
    },
    onSetActive: (active, reason) => engine?.setActive(active, /** @type {any} */ (reason)),
  });

  /** @type {(code: string, o: any) => void} */
  const reportError = (code, opt) => {
    const message = opt.messageKey ? i18n.t(opt.messageKey) : String(opt.message ?? code);
    bridge.emitError(code, { ...opt, message });
    telemetry.event('error', { code, fatal: Boolean(opt.fatal) });
    if (opt.fatal) shell?.showError({ code, message, retryable: Boolean(opt.retryable) });
  };

  // ---------- 1. WebGL2 ----------
  if (!hasWebGL2()) {
    bridge.emitError('WEBGL_UNSUPPORTED', {
      fatal: true,
      retryable: false,
      message: i18n.t('err.webglUnsupported'),
    });
    showStaticFallback(o.root, { messageKey: 'err.webglUnsupported', t: i18n.t, info: o.info });
    throw new Error('WEBGL_UNSUPPORTED');
  }

  // ---------- 2. Engine ----------
  const canvas = document.createElement('canvas');
  canvas.id = 'webgl';
  const stage = document.createElement('div');
  stage.className = 'ol-stage';
  stage.appendChild(canvas);
  o.root.appendChild(stage);

  // near/far/exposure: Engine đã nhận sẵn trong config (xem typedef EngineConfig) nhưng
  // trước đây boot KHÔNG truyền xuống, nên mọi tiết bị khoá cứng near 0.6 / far 100.
  // Đó là ngưỡng phù hợp cho tiết dạng "một vật thể ở giữa" (quả cầu), nhưng KHÔNG đủ
  // cho tiết dạng CẢNH có vòm trời bao quanh: đo trên tiết his-06-0001, vòm trời bán
  // kính 67.7 đơn vị model trong khi các điểm nhìn chỉ cách tâm 0.47–4.31 — muốn cả hai
  // nằm trong (near, far) thì phải mở ngưỡng ra, không có hệ số co model nào thoả được.
  // Tiết khai qua `meta.engine`; thiếu thì giữ nguyên mặc định của Engine.
  engine = new Engine(canvas, {
    clearColor: settings.get('background'),
    ...(o.lesson.meta?.engine ?? {}),
  });
  tracker.onDispose(() => engine?.dispose());

  const q = detectQuality(engine.renderer);
  // Phơi tier ra CSS để shell tắt được hiệu ứng đắt (backdrop-filter) trên máy yếu.
  o.root.classList.add(`is-tier-${q.tier}`);
  engine.renderer.setPixelRatio(Math.min(window.devicePixelRatio, q.pixelRatioMax));
  // Trần AN TOÀN cho máy yếu, cùng tinh thần với pixelRatioMax: 2048² depth map trên GPU
  // tích hợp 4 nhân là tốn hơn cả cảnh chính. 1024 vẫn đủ mềm cho bóng của bài giảng.
  if (q.tier === 'low') engine.setShadowMapSize(1024);
  telemetry.event('quality', /** @type {any} */ (q.info));

  tracker.onDispose(
    watchContextLoss(canvas, {
      onLost: () => {
        engine?.stop();
        reportError('CONTEXT_LOST', {
          fatal: false,
          retryable: false,
          messageKey: 'err.contextLost',
        });
      },
      onRestored: () => engine?.start(),
    }),
  );

  // ---------- 3. Shell (taskbar, loading, info, settings) ----------
  const shell = mountShell({
    root: o.root,
    stage,
    engine,
    t: i18n.t,
    settings,
    emitter,
    info: o.info,
    languages,
    onResetView: () => engine?.resetView(),
    onToggleRotate: (on) => emitter.emit('lesson:rotate', on),
  });
  tracker.onDispose(() => shell.dispose());

  // ---------- 4. Asset ----------
  const manager = new THREE.LoadingManager();
  manager.onProgress = (_url, done, total) => shell.loading.setProgress(total ? done / total : 0);
  manager.onError = (url) =>
    reportError('ASSET_404', {
      fatal: false,
      retryable: true,
      messageKey: 'err.assetFailed',
      detail: url,
    });

  const assets = createAssetLoader({
    manifest: o.manifest,
    manager,
    renderer: engine.renderer,
    track: tracker.track,
    reportError: (code, opt) => reportError(code, opt),
    baseUrl: o.baseUrl ?? '/',
  });

  // Hiện màn hình tải NGAY (createAssetLoader là đồng bộ, chưa có await nào ở trên).
  // Ảnh nền lấy từ khoá quy ước LOADING_BG_KEY nếu tiết có khai — dùng assets.url() để
  // không nhân bản logic dựng URL, và không gọi assets.texture() vì CSS tự tải lấy.
  const hasLoadingBg = Boolean(o.manifest?.assets?.[LOADING_BG_KEY]);
  shell.loading.show({
    title: o.info?.title?.[lang] ?? o.info?.title?.vi ?? '',
    subtitle: i18n.t('loading.subtitle'),
    backgroundUrl: hasLoadingBg ? assets.url(LOADING_BG_KEY) : undefined,
    baseUrl: o.baseUrl ?? '/',
  });

  // Audio: một AudioContext dùng chung, tự nghe settings.sound/volume, tự xử lý
  // chính sách autoplay. Xem chú thích đầu core/audio.js.
  const audio = createAudio({ settings, assets, reportError });
  tracker.onDispose(() => audio.dispose());

  const picker = createPicker({ camera: engine.camera, dom: engine.renderer.domElement });
  tracker.onDispose(() => picker.dispose());
  engine.onUpdate(() => picker.update());

  // ---------- 5. ctx ----------
  const progress = createProgress({
    lessonId: o.lesson.meta.id,
    bridge,
    params,
    isActive: () => Boolean(engine?.isActive()),
  });

  /** @type {LessonContext} */
  const ctx = {
    THREE,
    renderer: engine.renderer,
    scene: engine.scene,
    camera: engine.camera,
    controls: engine.controls,
    engine,
    canvas,
    domRoot: stage,
    assets,
    audio,
    t: i18n.t,
    lang,
    ui: shell.ui,
    progress,
    telemetry,
    settings,
    events: emitter,
    picker,
    reportError,
    track: tracker.track,
    onDispose: tracker.onDispose,
    signal: abort.signal,
    params,
    quality: q.tier,
    get isActive() {
      return Boolean(engine?.isActive());
    },
    get reducedMotion() {
      return Boolean(settings.get('reducedMotion'));
    },
    get viewport() {
      return {
        width: stage.clientWidth,
        height: stage.clientHeight,
        dpr: engine?.renderer.getPixelRatio() ?? 1,
      };
    },
  };

  // settings → engine
  tracker.onDispose(settings.onChange('background', (v) => engine?.setBackground(String(v))));
  tracker.onDispose(
    settings.onChange('lang', (v) => {
      if (!i18n.has(String(v))) return;
      i18n.setDict(String(v), mergedDicts[String(v)] ?? {});
      shell.retranslate();
      emitter.emit('lesson:lang', v);
    }),
  );
  engine.setBackground(String(settings.get('background')));

  // ---------- 6. Kiểm engineRange ----------
  if (!satisfiesEngineRange(o.lesson.meta.engineRange, ENGINE_API)) {
    reportError('INIT_FAILED', {
      fatal: true,
      retryable: false,
      messageKey: 'err.initFailed',
      detail: `engineRange ${o.lesson.meta.engineRange} không khớp engineApi ${ENGINE_API}`,
    });
    throw new Error('engineRange mismatch');
  }

  // ---------- 7. lesson.init() ----------
  try {
    await o.lesson.init(ctx);
  } catch (err) {
    reportError('INIT_FAILED', {
      fatal: true,
      retryable: true,
      messageKey: 'err.initFailed',
      detail: String(/** @type {any} */ (err)?.stack ?? err),
    });
    throw err;
  }
  if (abort.signal.aborted) return { ctx, engine, dispose: () => {} };

  // ---------- 8. update loop của tiết ----------
  if (o.lesson.update) {
    engine.onUpdate((dt, elapsed) => {
      try {
        o.lesson.update?.(dt, elapsed, ctx);
      } catch (err) {
        engine?.stop();
        reportError('RUNTIME_ERROR', {
          fatal: false,
          retryable: false,
          messageKey: 'err.runtime',
          detail: String(/** @type {any} */ (err)?.stack ?? err),
        });
      }
    });
  }
  engine.onResize = (w, h) => {
    shell.onResize(w, h);
    o.lesson.onResize?.(w, h, ctx);
  };

  // ---------- 9. ready ----------
  // TRÌNH TỰ QUAN TRỌNG. Trước đây gọi hide() ngay sau init(), tức màn hình tải bắt
  // đầu mờ đi khi engine CHƯA vẽ frame nào có mô hình — nên trong 350ms mờ đó học
  // sinh thấy mô hình đang hiện dần cùng lúc với poster, trông như tải chưa xong.
  // Giờ chờ engine vẽ thật ≥2 frame (mô hình đã lên GPU, đã có pixel) rồi mới mở màn.
  // waitFrames LÀ thứ làm nhịp mở bài mượt: khi hide() bắt đầu mờ lớp phủ ra thì cảnh 3D
  // phía sau đã render xong, đủ texture, đục hoàn toàn — lớp phủ mờ đi là lộ ra một cảnh
  // hoàn chỉnh. KHÔNG thêm một nhịp fade nữa cho canvas/mô hình ở đây: đã thử ba lần và
  // lần nào cũng thành lỗi hiển thị (nhấp nháy / mô hình bạc màu / nền trống trắng mờ) —
  // xem mục "Nhịp MỞ BÀI" trong shell.css.
  await waitFrames(engine, 2, 1500);
  await shell.loading.hide();
  shell.markReady(); // chrome trôi vào chỗ SAU khi màn hình tải đã tắt
  const bootMs = Math.round(performance.now() - t0);
  bridge.emitReady({
    activeLanguage: lang,
    bootMs,
    qualityTier: q.tier,
    hasProgress: true,
    preferredAspect: { width: 16, height: 9 },
  });
  telemetry.timing('boot', bootMs);

  // ---------- dispose ----------
  const dispose = () => {
    abort.abort();
    o.lesson.dispose?.();
    tracker.disposeAll();
    bridge.dispose();
    emitter.clear();
    stage.remove();
  };

  // Debug + smoke test hook. LUÔN gắn (rất nhỏ) — smoke test cần nó ở production.
  /** @type {any} */ (window).__ol = {
    ctx,
    engine,
    bridge,
    tracker,
    i18n,
    telemetry,
    dispose,
    stats: () => engine?.stats(),
    runFrames: (n = 60) => {
      for (let i = 0; i < n; i++) engine?.renderOnce();
      return engine?.stats();
    },
  };

  if (debug) {
    const { mountDebugPanel } = await import('../debug/DebugPanel.js');
    tracker.onDispose(
      mountDebugPanel({
        root: o.root,
        engine,
        ctx,
        bridge,
        quality: q,
        lessonId: o.lesson.meta.id,
      }),
    );
  }

  return { ctx, engine, dispose };
}

/**
 * Chờ engine vẽ xong ít nhất `n` frame THẬT, để mô hình đã có pixel trước khi mở
 * màn hình tải. Dùng engine.frameCount chứ không đếm requestAnimationFrame suông —
 * engine có thể đang tạm dừng (tab ẩn / iframe bị che) và lúc đó rAF vẫn chạy
 * nhưng KHÔNG có frame nào được vẽ.
 *
 * Luôn có timeout: tab ẩn thì rAF bị treo, không được để boot đứng mãi.
 * @param {{frameCount: number}} engine
 * @param {number} n
 * @param {number} timeoutMs
 */
function waitFrames(engine, n, timeoutMs) {
  const target = (engine.frameCount ?? 0) + n;
  return new Promise((resolve) => {
    const t0 = performance.now();
    const tick = () => {
      if ((engine.frameCount ?? 0) >= target || performance.now() - t0 > timeoutMs) {
        resolve(undefined);
        return;
      }
      requestAnimationFrame(tick);
    };
    // setTimeout song song: rAF không chạy khi tab ẩn, timeout mới cứu được.
    setTimeout(() => resolve(undefined), timeoutMs);
    requestAnimationFrame(tick);
  });
}

/** @returns {boolean} */
function hasWebGL2() {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2'));
  } catch {
    return false;
  }
}

/**
 * So khớp engineRange rất đơn giản: chỉ kiểm MAJOR.
 * Đủ cho v1; nâng lên semver đầy đủ khi có core version thứ 3 lưu hành.
 * @param {string} range  vd '^0.1.0'
 * @param {string} api    vd '0.1'
 */
export function satisfiesEngineRange(range, api) {
  const m = /(\d+)\.(\d+)/.exec(range);
  const a = /(\d+)\.(\d+)/.exec(api);
  if (!m || !a) return false;
  if (m[1] !== a[1]) return false; // major phải khớp
  return Number(a[2]) >= Number(m[2]); // engine minor ≥ minor tiết yêu cầu
}
